import { Chip, Stack, Typography, Box, Paper } from "@mui/material";
import React from "react";
import Counter, { CounterDelegate } from "../SharedComponents/Counter";
import { ICartItem } from "../SharedComponents/CartSingleton";

interface State { }

interface Props {
  item: (ICartItem & { description?: string });
  delegate: CheckoutReviewRowDelegate;
  disabled?: boolean;
  index: number;
}

export interface CheckoutReviewRowDelegate {
  didChangeQuantity(instrumentId: number, quantity: number): void;
}

class CheckoutReviewRow
  extends React.Component<Props, State>
  implements CounterDelegate {
  constructor(props: Props) {
    super(props);

    this.state = {};
  }

  didSetValueTo(value: number): void {
    this.props.delegate.didChangeQuantity(this.props.item.instrumentId, value);
  }

  render() {
    const { item, disabled, index } = this.props;
    const currencyFormatter = new Intl.NumberFormat("pt-br", {
      style: "currency",
      currency: "BRL",
    });
    const discountedUnitPrice = currencyFormatter.format(
      item.discountedUnitPriceCents / 100
    );
    const originalUnitPrice = currencyFormatter.format(item.unitPriceCents / 100);
    const discountPct = item.appliedDiscountPct;
    const hasDiscount = discountPct != null && discountPct > 0;
    const formattedDiscountPct = hasDiscount
      ? new Intl.NumberFormat("pt-BR", {
          maximumFractionDigits: 2,
          minimumFractionDigits: 0,
        }).format(discountPct)
      : null;
    const previousPrice =
      item.priceSnapshotCents != null
        ? currencyFormatter.format(item.priceSnapshotCents / 100)
        : null;
    const discountedSubtotal = currencyFormatter.format(
      item.discountedSubtotalCents / 100
    );
    const originalSubtotal = currencyFormatter.format(item.subtotalCents / 100);
    const isEven = index % 2 === 1;

    return (
      <Paper
        elevation={0}
        sx={{
          backgroundColor: isEven
            ? "rgba(242,249,248,0.55)"
            : "rgba(255,255,255,0.92)",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(30,160,151,0.08)",
          },
          borderRadius: 3,
          px: { xs: 1.5, md: 3 },
          py: { xs: 1.5, md: 2 },
          display: "flex",
          flexDirection: "column",
          gap: { xs: 2, md: 2 },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: 3 }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "flex-start" }}
        >
          <Stack flex={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              {item.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.description || "Descrição indisponível"}
            </Typography>
          </Stack>

          <Box sx={{ maxWidth: { xs: "100%", sm: 200 } }}>
            <Counter
              minValue={0}
              startValue={item.quantity}
              delegate={this}
              disabled={disabled}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 2, sm: 3 }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Stack spacing={1} alignItems={{ xs: "flex-start" }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              {item.priceChanged && (
                <Chip
                  label="Preço atualizado"
                  color="warning"
                  size="small"
                  sx={{ width: "fit-content" }}
                />
              )}
            </Stack>
            <Stack spacing={0.25}>
              <Typography variant="body1" fontWeight={700} color="primary">
                {discountedUnitPrice}
              </Typography>
              {hasDiscount && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textDecoration: "line-through" }}
                  >
                    {originalUnitPrice}
                  </Typography>
                  {formattedDiscountPct && (
                    <Chip
                      label={`-${formattedDiscountPct}%`}
                      color="warning"
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Stack>
              )}
            </Stack>
            {item.priceChanged && previousPrice && (
              <Typography variant="caption" color="text.secondary">
                Preço unitário anterior: {previousPrice}
              </Typography>
            )}
          </Stack>
          <Stack
            spacing={0.5}
            alignItems={{ xs: "flex-start", sm: "flex-end" }}
            sx={{ flexGrow: { xs: 0, sm: 1 } }}
          >
            <Typography variant="body2" color="text.secondary">
              Subtotal
            </Typography>
            <Stack spacing={0.25} alignItems={{ xs: "flex-start", sm: "flex-end" }}>
              <Typography variant="body1" fontWeight={700} color="primary">
                {discountedSubtotal}
              </Typography>
              {hasDiscount && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textDecoration: "line-through" }}
                >
                  {originalSubtotal}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    );
  }
}

export default CheckoutReviewRow;
